import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CustomButton from "components/common/Button";
import { AlertType } from "components/common/Snackbar";
import { showSnackbar } from "store/actions/Utility";
import { updateBillingSubscriptionStatus } from "services/billing";

import { CustomChip } from "./ui";
import { BsSubscription, formatDate, statusColor, titleCase } from "./types";

/**
 * [PRD-2043] Pause / resume a subscription with a mandatory note.
 * The note (why) + the acting Edge user (who) are recorded on the billing-service
 * audit trail + the subscription metadata.
 */
export default function SubscriptionStatusModal({
  subscription,
  onClose,
  onDone,
}: {
  subscription: BsSubscription;
  onClose: () => void;
  onDone: () => void;
}) {
  const target: "active" | "paused" = subscription.status === "active" ? "paused" : "active";
  const actionLabel = target === "paused" ? "Pause" : "Resume";

  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  const handleConfirm = async () => {
    if (!note.trim() || submitting) return;
    setSubmitting(true);
    try {
      await updateBillingSubscriptionStatus(subscription.id, { status: target, note: note.trim() });
      onDone();
      onClose();
    } catch (err) {
      // [PRD-3348] Close the dialog and surface billing-service's reason (e.g.
      // a 409/422 rejection) in the error snackbar; otherwise use generic copy.
      const serverMessage = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      const message =
        typeof serverMessage === "string" && serverMessage.trim()
          ? serverMessage
          : "Failed to update the subscription. Please try again.";
      setSubmitting(false);
      onClose();
      dispatch(showSnackbar({ type: AlertType.error, message, dispatch }));
    }
  };

  // [PRD-3348] Explain what pause and resume actually do to billing. A due
  // date before today (UTC) means the resume rolls the anchor forward, so the
  // next charge lands on the following cycle rather than that stale date.
  const resumeCopy = (() => {
    const next = subscription.nextBillingDate;
    if (!next) return "No past months will be charged. The next charge is on the next due date.";
    const startOfTodayUtc = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00.000Z");
    if (new Date(next).getTime() < startOfTodayUtc.getTime()) {
      return "No past months will be charged. The next charge moves to the following cycle's due date.";
    }
    return `No past months will be charged. The next charge is on ${formatDate(next)}.`;
  })();
  const explainer =
    target === "paused"
      ? "Pausing stops charges. On each due date while paused, that month's invoice is voided and a draft is created for the following month."
      : resumeCopy;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div className="flex justify-between items-center">
          <p className="heading-h4-semibold color-text-heading-primary">{actionLabel} subscription</p>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-05" style={{ flexWrap: "wrap" }}>
            <span className="para-body-m-regular color-text-body-secondary">
              {subscription.customerName || subscription.planName} — current status:
            </span>
            <CustomChip
              label={titleCase(subscription.status)}
              size="small"
              color={statusColor(subscription.status)}
            />
          </div>

          <p className="para-body-s-regular color-text-body-secondary">
            {explainer}
          </p>

          <label className="para-body-s-medium color-text-body-primary" htmlFor="sub-status-note">
            Note (required) — why are you {target === "paused" ? "pausing" : "resuming"}?
          </label>
          <textarea
            id="sub-status-note"
            className="border rounded-lg p-075 para-body-m-regular"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Customer requested a hold for August"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton label="Cancel" onClick={onClose} />
        <CustomButton
          label={submitting ? "Saving…" : `${actionLabel} subscription`}
          onClick={handleConfirm}
          disabled={submitting || !note.trim()}
        />
      </DialogActions>
    </Dialog>
  );
}
